import { useEffect, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import cookie from "react-cookies";
import DataServiceSQL from "../../DataService/DataServiceSQL.js";

/**
 * Loads the connected user once Keycloak authentication is ready, then reports
 * the locale and the disclaimer state to the parent.
 *
 * It MUST live inside <ReactKeycloakProvider>: App itself renders the provider,
 * so App cannot observe when login completes. Firing the account request on App
 * mount (before PrivateRoute saves the token cookie) is why the disclaimer popup
 * only appeared after an F5 refresh — when the cookie was already persisted —
 * and never right after login, when the request went out as "Bearer undefined"
 * and failed silently.
 *
 * Renders nothing; it only drives the parent's state via the passed setters.
 */
const UserAccountGate = ({ setCurrentLocale, setDisclaimerUsername, setShowDisclaimer }) => {
    const { keycloak, initialized } = useKeycloak();
    const loaded = useRef(false);

    useEffect(() => {
        if (!initialized || !keycloak.authenticated || !keycloak.token) return;
        if (loaded.current) return;
        loaded.current = true;

        // Guarantee the token cookie the HTTP layer reads is set before the
        // request, independently of PrivateRoute's render timing.
        cookie.save('token', keycloak.token, { path: '/' });

        DataServiceSQL.getUserAccountReq_fhir()
            .then((response) => {
                const user = response.data.entry[0];
                const locale = user.communication.language.text;
                if (locale) setCurrentLocale(locale);
                if (!user.disclaimer_accepted_at) {
                    setDisclaimerUsername(user.username || null);
                    setShowDisclaimer(true);
                }
            })
            .catch((e) => {
                console.log(e);
            });
    }, [initialized, keycloak.authenticated, keycloak.token, setCurrentLocale, setDisclaimerUsername, setShowDisclaimer]);

    return null;
};

export default UserAccountGate;
